import { AfterViewInit, Component, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SignupComponentStore } from './signup.component.store';
import {
  PaymentIntentResult,
  SetupIntentResult,
  StripeElementsOptions,
  StripePaymentElementOptions,
} from '@stripe/stripe-js';
import { StripePaymentElementComponent, NgxStripeModule, StripeService, injectStripe } from 'ngx-stripe';
import { CustomRendererService } from '@shared/services/custom-renderer.service';
import { Observable, of } from 'rxjs';
import { ActivatedRoute, Router, UrlTree } from '@angular/router';
import { SharedModule } from '@shared/shared.module';
import {
  IOnboardingSaga,
  OnboardingCountryInterface,
  OnboardingSagaStep,
  IOnboardingDiscount,
  SignupPaymentData,
  SignupRegistrationData,
  SignupSubscriptionData,
  OnboardingDocusign,
} from '@shared/interfaces/onboarding-saga.interface';
import { SignupPlanComponent } from './signup-plan/signup-plan.component';
import { SignupRegisterComponent } from './signup-register/signup-register.component';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { SignupSubscriptionComponent } from './signup-subscription/signup-subscription.component';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { AppConstants } from '@core/constants';
import { createNullableState, createState } from '@shared/helpers/create-state.helper';

@Component({
  selector: 'app-signup',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SharedModule,
    NgxStripeModule,
    SignupPlanComponent,
    SignupRegisterComponent,
    SignupSubscriptionComponent,
  ],
  providers: [SignupComponentStore],
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.scss'],
})
export class SignupComponent implements OnInit, AfterViewInit {
  @ViewChild(SignupRegisterComponent) registrationComponent!: SignupRegisterComponent;
  @ViewChild(SignupSubscriptionComponent) subscriptionComponent!: SignupSubscriptionComponent;
  @ViewChild(StripePaymentElementComponent) paymentElement!: StripePaymentElementComponent;

  public store = inject(SignupComponentStore);

  // Services
  private readonly customRendererService = inject(CustomRendererService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly fb = inject(FormBuilder);
  private readonly stripeService = inject(StripeService);
  private readonly snackBar = inject(MatSnackBar);
  private router = inject(Router);

  // State
  private guid = createNullableState<string>();
  private intentType = createState<string>('payment');
  clientSecret = createNullableState<string>();
  stepperIndex = createState<number>(0);
  disablePurchaseButton = createState<boolean>(false);
  docusignEvent = createNullableState<string>();
  contractSigned = createState<boolean>(false);
  buttonState = createState({
    account_creation: {
      disabled: false,
      text: 'Sign up',
    },
    payment_preprocessing: {
      disabled: false,
      text: 'Confirm',
    },
    contract_signing: {
      disabled: false,
      text: 'Sign contract',
    },
  });

  // Forms
  public elementsOptions: StripeElementsOptions = { locale: 'en' };
  public paymentElementOptions: StripePaymentElementOptions | null = null;
  public signupForm: FormGroup;
  public readonly stripe = injectStripe();

  public termsLink = window.location.origin + '/terms';
  public privacyPolicyLink = window.location.origin + '/privacy-policy';

  ngOnInit(): void {
    this.setTheme();
    this.initStripeOption();
    this.assignGuidFromRoute();
    if (!this.guid.value) {
      return;
    }
    this.assignDocusignEventFromRoute();
    this.initForm();
    this.setupListeners();

    this.store.loadStore(this.guid.value);
  }

  ngAfterViewInit(): void {
    this.handleDocusignEvent();
  }

  public signupUser(): void {
    const signupData = {
      ...this.registrationControl?.value,
      guid: this.guid.value,
    };
    this.buttonState.update((buttons) => ({
      ...buttons,
      account_creation: { text: 'Please wait...', disabled: true },
    }));
    this.store.signupUser(signupData).subscribe({
      next: (response) => {
        this.store.setSaga(response.saga as IOnboardingSaga);
        this.buttonState.update((buttons) => ({
          ...buttons,
          account_creation: {
            text: 'Sign up complete',
            disabled: true,
          },
        }));
      },
      error: (error) => {
        this.buttonState.update((buttons) => ({
          ...buttons,
          account_creation: {
            text: 'Sign up',
            disabled: false,
          },
        }));
        if (error?.error?.email) {
          const emailControl = this.registrationComponent.form.get('email');
          if (emailControl) {
            emailControl.setErrors({ email_exists: true });
            emailControl.markAsTouched();
          }
        } else {
          console.error(error);
        }
      },
    });
  }

  public updateSubscription(): void {
    const configureSignupData = {
      ...this.subscriptionControl?.value,
      email: this.registrationControl?.value?.email,
      guid: this.guid.value,
    };
    if (configureSignupData.sports) {
      configureSignupData.sports = configureSignupData.sports.map((v: any) => v.value);
    }
    this.buttonState.update((buttons) => ({
      ...buttons,
      payment_preprocessing: { text: 'Please wait...', disabled: true },
    }));

    this.store.configureSignup(configureSignupData).subscribe({
      next: (response) => {
        this.store.setSaga(response.saga as IOnboardingSaga);
        this.buttonState.update((buttons) => ({
          ...buttons,
          payment_preprocessing: {
            text: 'Subscription details confirmed',
            disabled: true,
          },
        }));
      },
      error: (error) => {
        console.error(error);
        this.buttonState.update((buttons) => ({
          ...buttons,
          payment_preprocessing: {
            text: 'Confirm',
            disabled: false,
          },
        }));
      },
    });
  }

  public signContract(): void {
    this.buttonState.update((buttons) => ({
      ...buttons,
      contract_signing: { text: 'Fetcing contract link...', disabled: true },
    }));
    this.store.getContractLink(this.guid.value!).subscribe({
      next: ({ signing_url }) => {
        if (signing_url) {
          this.buttonState.update((buttons) => ({
            ...buttons,
            contract_signing: { text: 'Redirecting to contract...', disabled: true },
          }));
          window.location.href = signing_url;
        }
      },
      error: (error) => {
        console.error(error);
        this.snackBar.open(error, 'OK', AppConstants.TOAST_CONFIG.ERROR);
      },
    });
  }

  public verifyContract(docusignDetails?: OnboardingDocusign): void {
    const contractData = {
      guid: this.guid.value!,
      envelope_id: docusignDetails?.envelope_id,
    };
    this.buttonState.update((buttons) => ({
      ...buttons,
      contract_signing: { text: 'Please wait...', disabled: true },
    }));
    this.store.verifyContract(contractData).subscribe({
      next: (response) => {
        this.store.setSaga(response.saga as IOnboardingSaga);
        this.buttonState.update((buttons) => ({
          ...buttons,
          contract_signing: {
            text: 'Contract verified',
            disabled: true,
          },
        }));
      },
      error: (error) => {
        console.error(error);
        this.buttonState.update((buttons) => ({
          ...buttons,
          contract_signing: {
            text: 'Verify and continue',
            disabled: false,
          },
        }));
      },
    });
  }

  public handlePayment(): void {
    const paymentData: SignupPaymentData = {
      guid: this.guid.value!,
      email: this.registrationComponent.form.get('email')?.value!,
    };
    this.disablePurchaseButton.set(true);

    if (this.intentType.value === 'payment') {
      this.stripeService
        .confirmPayment({
          elements: this.paymentElement.elements,
          clientSecret: this.paymentElement.clientSecret,
          confirmParams: {
            return_url: window.location.href,
          },
          redirect: 'if_required',
        })
        .subscribe({
          next: (response: PaymentIntentResult) => {
            if (response.error) {
              const errorMessage = response.error.message ?? 'Unable to confirm payment at this time';
              this.snackBar.open(errorMessage, 'OK', AppConstants.TOAST_CONFIG.ERROR);
              this.disablePurchaseButton.set(false);
              this.store.signupPayment(paymentData, response);
            } else if (response.paymentIntent?.status === 'succeeded') {
              // Update saga
              this.store.signupPayment(paymentData, response);
            }
          },
          error: (error) => {
            this.disablePurchaseButton.set(false);
            this.snackBar.open(error, 'OK', AppConstants.TOAST_CONFIG.ERROR);
            paymentData.error_message = error;
            this.store.signupPayment(paymentData);
          },
        });
    } else {
      this.stripeService
        .confirmSetup({
          elements: this.paymentElement.elements,
          clientSecret: this.paymentElement.clientSecret,
          confirmParams: {
            return_url: window.location.href,
          },
          redirect: 'if_required',
        })
        .subscribe({
          next: (response: SetupIntentResult) => {
            if (response.error) {
              const errorMessage = response.error.message ?? 'Unable to capture payment method at this time';
              this.snackBar.open(errorMessage, 'OK', AppConstants.TOAST_CONFIG.ERROR);
              this.disablePurchaseButton.set(false);
              this.store.signupPayment(paymentData, undefined, response);
            } else if (response.setupIntent?.status === 'succeeded') {
              // Update saga
              this.store.signupPayment(paymentData, undefined, response);
            }
          },
          error: (error) => {
            this.disablePurchaseButton.set(false);
            this.snackBar.open(error, 'OK', AppConstants.TOAST_CONFIG.ERROR);
            paymentData.error_message = error;
            this.store.signupPayment(paymentData);
          },
        });
    }
  }

  public appLogin(): void {
    this.router.navigateByUrl('/home/dashboard');
  }

  public filterCountries(saga: IOnboardingSaga, countries: OnboardingCountryInterface[]): OnboardingCountryInterface[] {
    if (!countries) {
      return [];
    }

    const planRegions = saga.plan.plan_prices?.map((p) => p.region);
    return countries.filter((c) => planRegions?.includes(c.region));
  }

  public hasDiscount(saga?: IOnboardingSaga): IOnboardingDiscount {
    if (!saga) {
      return {};
    }

    return {
      type: saga.link.discount_type,
      apply_for_days: saga.link.apply_for_days,
      value: saga.link.discount_value,
    };
  }

  public getSelectedCountry(saga?: IOnboardingSaga): number {
    if (saga?.subscription_details?.billing_country) {
      return saga.subscription_details?.billing_country;
    }

    if (!this.subscriptionControl?.value) {
      return -1;
    }

    let billingCountry = this.subscriptionControl?.value.billing_country;
    if (typeof billingCountry === 'string') {
      billingCountry = parseInt(billingCountry !== '' ? billingCountry : '-1');
    }
    return billingCountry;
  }

  public getSelectedPeriod(saga?: IOnboardingSaga): number {
    if (saga?.subscription_details?.billing_period) {
      return saga.subscription_details?.billing_period;
    }

    if (!this.subscriptionControl?.value) {
      return 1;
    }

    return this.subscriptionControl?.value.billing_period;
  }

  getStepCompleted(index: number): boolean {
    return index < this.stepperIndex.value;
  }

  getStepEditable = (index: number): boolean => index >= this.stepperIndex.value;

  get registrationControl() {
    return this.signupForm.get('registration');
  }

  get subscriptionControl() {
    return this.signupForm.get('subscription');
  }

  private initStripeOption(): void {
    this.elementsOptions = {
      locale: 'en',
      appearance: {
        theme: 'night',
      },
    };
  }

  private initForm(): void {
    this.signupForm = this.fb.group({
      registration: this.fb.control<SignupRegistrationData | null>(null, Validators.required),
      subscription: this.fb.control<SignupSubscriptionData | null>(
        {
          billing_period: 1,
          vat_required: false,
        },
        Validators.required
      ),
      contract: this.fb.control<null>(null),
      payment: this.fb.control<null>(null),
    });
  }

  private assignGuidFromRoute(): void {
    const guid = (this.activatedRoute.snapshot.params?.guid as string) ?? null;
    this.guid.set(guid);
  }

  private assignDocusignEventFromRoute(): void {
    this.docusignEvent.set(this.activatedRoute.snapshot?.queryParams?.event);
  }

  private setTheme(): void {
    this.customRendererService.removeClass('body', ['light-theme']);
    this.customRendererService.addClass('body', ['dark-theme']);
  }

  private sagaListener(saga: IOnboardingSaga): void {
    if (saga.current_step === OnboardingSagaStep.ACCOUNT_CREATION) {
      this.stepperIndex.set(0);
    } else if (saga.current_step === OnboardingSagaStep.PAYMENT_PREPROCESSING) {
      const { first_name, last_name, email } = saga.user;
      this.signupForm.patchValue({
        registration: {
          first_name,
          last_name,
          email,
          privacy_policy: true,
          terms: true,
        },
        subscription: {
          billing_country: -1,
          billing_period: 1,
        },
      });
      this.stepperIndex.set(1);
    } else if (saga.current_step === OnboardingSagaStep.CONTRACT_SIGNING) {
      const { first_name, last_name, email } = saga.user;
      this.signupForm.patchValue({
        registration: {
          first_name,
          last_name,
          email,
          privacy_policy: true,
          terms: true,
        },
        subscription: {
          billing_country: saga.subscription_details?.billing_country,
          billing_period: saga.subscription_details?.billing_period,
        },
      });
      this.stepperIndex.set(2);
    } else if (saga.current_step === OnboardingSagaStep.PAYMENT_PROCESSING) {
      const { first_name, last_name, email } = saga.user;
      this.signupForm.patchValue({
        registration: {
          first_name,
          last_name,
          email,
          privacy_policy: true,
          terms: true,
        },
        subscription: {
          billing_country: saga.subscription_details?.billing_country,
          billing_period: saga.subscription_details?.billing_period,
        },
      });
      const clientSecret = saga.payment?.client_secret!;
      this.intentType.set(saga.payment?.intent_type ?? this.intentType.value);
      this.clientSecret.set(clientSecret);
      this.elementsOptions.clientSecret = clientSecret;
      const billingCountry = saga.subscription_details?.billing_country_code;
      if (billingCountry != null) {
        this.paymentElementOptions = {
          defaultValues: {
            billingDetails: {
              address: {
                country: billingCountry,
              },
            },
          },
        };
      }
      this.stepperIndex.set(3);
    } else if (saga.current_step === OnboardingSagaStep.KYC) {
      const { first_name, last_name, email } = saga.user;
      this.signupForm.patchValue({
        registration: {
          first_name,
          last_name,
          email,
          privacy_policy: true,
          terms: true,
        },
        subscription: {
          billing_country: saga.subscription_details?.billing_country,
          billing_period: saga.subscription_details?.billing_period,
        },
      });
      this.stepperIndex.set(4);
    }
  }

  private handleDocusignEvent(): void {
    // If complete send call to backend
    // Backend call should update local saga as well
    // Update form flag to allow user to continue
    const eventValue = this.docusignEvent.value;

    switch (eventValue) {
      case 'ttl_expired':
        console.log('Link expired');
        break;
      case 'signing_complete': {
        this.contractSigned.set(true);
        this.buttonState.update((buttons) => ({
          ...buttons,
          contract_signing: {
            text: 'Verify and continue',
            disabled: false,
          },
        }));
        break;
      }
      default:
        break;
    }
  }

  private setupListeners(): void {
    this.store.viewState.$.subscribe((viewState) => {
      if (viewState.status === 'loaded') {
        this.sagaListener(viewState.data.saga!);
      }
    });
  }
}
